import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Tooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";

import type { IFoldersItem } from "../../../../types";

import {
    buildTreeRelationships,
    getDescendantsOnDemand,
    getTooltipContent,
    handleNormalSelection,
    handleSpecialCaseSelection,
    reconcileParentSelection,
    getOptimizedSelectionForSegment,
} from "./helpers";

export const useTableSelection = (data: IFoldersItem[]) => {
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const stableRelationships = useRef({
        childrenByParent: new Map<string, string[]>(),
        parentByChild: new Map<string, string>(),
    });

    const { childrenByParent, parentByChild } = useMemo(() => buildTreeRelationships(data), [data]);

    useEffect(() => {
        childrenByParent.forEach((children, parent) => {
            stableRelationships.current.childrenByParent.set(parent, children);
        });
        parentByChild.forEach((parent, child) => {
            stableRelationships.current.parentByChild.set(child, parent);
        });

        const selectedSet = new Set(selectedRowKeys);
        const newSelection = selectedRowKeys.filter((key) => {
            const children = stableRelationships.current.childrenByParent.get(key);
            if (!children) {
                return true;
            }

            const allChildrenSelected = children.every((child) => selectedSet.has(child));
            return allChildrenSelected;
        });

        if (newSelection.length !== selectedRowKeys.length) {
            setSelectedRowKeys(newSelection);
        }
    }, [childrenByParent, parentByChild, selectedRowKeys]);

    const selectedKeysSet = useMemo(() => new Set(selectedRowKeys), [selectedRowKeys]);

    const getCheckboxProps = useCallback(
        (record: IFoldersItem) => {
            return {
                onChange: (e: { target: { checked: boolean } }) => {
                    const willBeChecked = e.target.checked;
                    const isCurrentlySelected = selectedKeysSet.has(record.key);

                    // Get siblings
                    const parent = parentByChild.get(record.key);
                    const siblings = parent ? childrenByParent.get(parent) : undefined;

                    // Check if ALL siblings are currently selected
                    const allSiblingsSelected = siblings ? siblings.every((sib) => selectedKeysSet.has(sib)) : false;

                    // Clicking selected child when all siblings selected, Keep only that child, deselect all siblings
                    const isSpecialCase =
                        !willBeChecked &&
                        isCurrentlySelected &&
                        parent &&
                        allSiblingsSelected &&
                        siblings &&
                        siblings.length > 1;

                    const getDescendants = (key: string) => getDescendantsOnDemand(key, childrenByParent);
                    const reconcileParents = (selection: string[], changedKeys: string[]) =>
                        reconcileParentSelection(selection, changedKeys, childrenByParent, parentByChild);

                    if (isSpecialCase && siblings) {
                        const newSelection = handleSpecialCaseSelection(
                            record,
                            selectedRowKeys,
                            siblings,
                            getDescendants,
                            reconcileParents,
                        );
                        setSelectedRowKeys(newSelection);
                        return;
                    }

                    // Default behavior
                    const newSelection = handleNormalSelection(
                        record,
                        willBeChecked,
                        selectedRowKeys,
                        getDescendants,
                        reconcileParents,
                    );
                    setSelectedRowKeys(newSelection);
                },
            };
        },
        [selectedRowKeys, selectedKeysSet, parentByChild, childrenByParent],
    );

    const renderCell = useCallback(
        (checked: boolean, record: IFoldersItem, _index: number, originNode: React.ReactNode) => {
            const hasChildren = record.children && record.children.length > 0;

            if (!hasChildren) {
                return originNode;
            }

            const tooltipContent = getTooltipContent(checked, record, parentByChild, childrenByParent, selectedKeysSet);

            return (
                <Tooltip content={translate(tooltipContent)}>
                    <div>{originNode}</div>
                </Tooltip>
            );
        },
        [parentByChild, childrenByParent, selectedKeysSet],
    );

    const tableSelection = useMemo(
        () => ({
            enabled: true,
            selectedRowKeys,
            getCheckboxProps,
            hideSelectAll: true,
            onChange: (newSelectedRowKeys: string[]) => {
                setSelectedRowKeys(newSelectedRowKeys);
            },
            renderCell,
        }),
        [getCheckboxProps, selectedRowKeys, renderCell],
    );

    const selectedFoldersForSegment = useMemo(
        () =>
            getOptimizedSelectionForSegment(
                selectedRowKeys,
                stableRelationships.current.childrenByParent,
                stableRelationships.current.parentByChild,
            ),
        [selectedRowKeys],
    );

    return {
        selectedRowKeys,
        setSelectedRowKeys,
        tableSelection,
        selectedFoldersForSegment,
    };
};
